<template>
    <validation-observer ref="form" v-slot="{ handleSubmit, meta }">
        <modal
            :id="modalId"
            :title="title"
            ok-title="Save"
            :ok-disabled="!meta.valid || loading || !values.attestation"
            @ok.prevent="handleSubmit(_, save)"
            @hide="hideModal"
            size="lg"
            @shown="setData"
        >
            <busy-modal :show="loading" :overlay="true"></busy-modal>
            <b-form @submit.prevent="handleSubmit(_, save)" v-if="values">
                <b-row>
                    <b-col>
                        <validation-provider name="name" v-slot="{ errors, handleChange }" rules="required">
                            <form-input
                                label="Name:"
                                type="text"
                                v-model="values.name"
                                :error="errors[0]"
                                @input="handleChange"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="tax_type" v-slot="{ errors, handleChange }" rules="required">
                            <form-select
                                v-model="values.tax_type"
                                :options="taxTypeOptions"
                                label="Tax type:"
                                :error="errors[0]"
                                @input="handleChange"
                            >
                            </form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="amount_type" v-slot="{ errors, handleChange }" rules="required">
                            <form-select
                                v-model="values.amount_type"
                                :options="amountTypeOptions"
                                label="Type of charge:"
                                :error="errors[0]"
                                @change="handleChange"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                    <b-col>
                        <label>Amount:</label>
                        <validation-provider
                            name="amount"
                            v-slot="{ errors, handleChange }"
                            :rules="`required${
                                values.amount_type === PERCENT_PER_RESERVATION ? '|maxValueOrEqual:50' : ''
                            }`"
                        >
                            <form-input
                                v-if="values.amount_type === PERCENT_PER_RESERVATION"
                                class="mb-0"
                                type="number"
                                min="0.1"
                                max="50"
                                step="0.1"
                                v-model="values.amount"
                                right-text="%"
                                placeholder="A number between 0-50"
                                :error="errors[0]"
                                @input="handleChange"
                            />
                            <form-input
                                v-else
                                class="mb-0"
                                type="number"
                                min="0.1"
                                step="0.1"
                                :left-text="userCurrencySymbol"
                                v-model="values.amount"
                                :error="errors[0]"
                                @input="handleChange"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <div v-if="values.amount_type === PERCENT_PER_RESERVATION">
                    <b-row>
                        <b-col>
                            <label>What to tax:</label>
                            <validation-provider name="taxable_bases" v-slot="{ errors }">
                                <b-form-checkbox-group
                                    class="form-checkbox-group"
                                    v-model="values.taxable_bases"
                                    :options="taxableBasesOptions"
                                    :multiple="true"
                                    :error="errors[0]"
                                    stacked
                                >
                                    <b-form-invalid-feedback :state="taxableBasesState"
                                        >Please select at least one taxable base.</b-form-invalid-feedback
                                    >
                                </b-form-checkbox-group>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row class="mb-3">
                        <b-col>
                            <validation-provider name="max_cap_per_person_per_night" v-slot="{ errors }">
                                <form-input
                                    class="mb-0"
                                    placeholder="Maximum cap per person per night (optional)"
                                    type="number"
                                    label="Maximum cap per person per night:"
                                    min="0.1"
                                    step="0.1"
                                    v-model="values.max_cap_per_person_per_night"
                                    :error="errors[0]"
                                />
                            </validation-provider>
                            <a
                                class="subtitles"
                                href="https://support.lodgix.com/en/articles/5748549-airbnb-occupancy-taxes"
                                target="_blank"
                                rel="noopener noreferrer"
                                >Learn more</a
                            >
                        </b-col>
                    </b-row>
                </div>
                <b-row class="mb-3">
                    <b-col>
                        <validation-provider name="business_tax_id" v-slot="{ errors }">
                            <form-input
                                class="mb-0"
                                v-model="values.business_tax_id"
                                label="Business tax ID:"
                                placeholder="Business tax ID (optional)"
                                :error="errors[0]"
                            />
                        </validation-provider>
                        <span class="subtitles text-muted mt-1">
                            You can find this number on your tax registration documents.
                            <a
                                href="https://support.lodgix.com/en/articles/5748549-airbnb-occupancy-taxes"
                                target="_blank"
                                rel="noopener noreferrer"
                                >Learn more</a
                            >
                        </span>
                    </b-col>
                </b-row>
                <b-row class="mb-3">
                    <b-col>
                        <validation-provider
                            name="tot_registration_id"
                            v-slot="{ errors, handleChange }"
                            rules="required"
                        >
                            <form-input
                                class="mb-0"
                                v-model="values.tot_registration_id"
                                label="Accommodations tax registration number:"
                                placeholder="Accommodations tax registration number"
                                :error="errors[0]"
                                @input="handleChange"
                            />
                        </validation-provider>
                        <span class="subtitles text-muted mt-1">
                            You can find this number on your tax registration documents.
                            <a
                                href="https://support.lodgix.com/en/articles/5748549-airbnb-occupancy-taxes"
                                target="_blank"
                                rel="noopener noreferrer"
                                >Learn more</a
                            >
                        </span>
                    </b-col>
                </b-row>
                <b-row class="mb-3">
                    <b-col>
                        <validation-provider name="long_term_stay_exemption" v-slot="{ errors }">
                            <form-input
                                class="mb-0"
                                v-model="values.long_term_stay_exemption"
                                placeholder="Long term stay exemption (optional)"
                                label="Long term stay exemption:"
                                type="number"
                                :empty-value="null"
                                :error="errors[0]"
                            />
                        </validation-provider>
                        <span class="subtitles text-muted mt-1">
                            The value for this field is between 13 and 185 days. If not set, no exemption applies and
                            taxes are applied to all bookings regardless of length. This field and "Only first nights
                            exemption" cannot be both set.
                            <a
                                href="https://support.lodgix.com/en/articles/5748549-airbnb-occupancy-taxes"
                                target="_blank"
                                rel="noopener noreferrer"
                                >Learn more</a
                            >
                        </span>
                    </b-col>
                </b-row>
                <b-row
                    class="mb-3"
                    v-if="
                        values.amount_type === PERCENT_PER_RESERVATION ||
                        values.amount_type === FLAT_PER_GUEST_PER_NIGHT
                    "
                >
                    <b-col>
                        <validation-provider name="only_first_nights_exemption" v-slot="{ errors }">
                            <form-input
                                class="mb-0"
                                v-model="values.only_first_nights_exemption"
                                placeholder="Only first nights exemption (optional)"
                                label="Only first nights exemption:"
                                type="number"
                                :empty-value="null"
                                :error="errors[0]"
                            />
                        </validation-provider>
                        <a
                            class="subtitles"
                            href="https://support.lodgix.com/en/articles/5748549-airbnb-occupancy-taxes"
                            target="_blank"
                            rel="noopener noreferrer"
                            >Learn more</a
                        >
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <label>Terms for adding taxes:</label>
                        <form-checkbox type="checkbox" v-model="values.attestation">
                            <span class="subtitles text-muted">
                                I confirm this tax amount is correct, and I will pay this tax once remitted to me by
                                Airbnb. I grant Airbnb permission to disclose tax-related and transaction information
                                (such as listing address, tax amount, and Tax ID) to the relevant tax authorities.
                            </span>
                        </form-checkbox>
                    </b-col>
                </b-row>
            </b-form>
        </modal>
    </validation-observer>
</template>

<script>
    import isEmpty from 'lodash/isEmpty';
    import { mapGetters } from 'vuex';
    import {
        PARTNER_AIRBNB_AMOUNT_TYPE_MAPPING,
        PARTNER_AIRBNB_TAXABLE_BASES_MAPPING,
        PARTNER_AIRBNB_TAX_TYPE_MAPPING,
        PARTNER_AIRBNB_AMOUNT_TYPE_PERCENT_PER_RESERVATION,
        PARTNER_AIRBNB_AMOUNT_TYPE_FLAT_PER_GUEST_PER_NIGHT,
    } from '../../../../constants';
    import { formatResponseError } from '../../../../utils/api';
    import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
    import Modal from '../../../generic/modal/modal.vue';
    import FormInput from '../../../generic/form-input/form-input.vue';
    import FormSelect from '../../../generic/form-select/form-select.vue';
    import FormCheckbox from '../../../generic/form-checkbox/form-checkbox.vue';

    export default {
        name: 'PartnerAirbnbOccupancyTaxDetailsModalForm',
        components: {
            BusyModal,
            Modal,
            FormInput,
            FormSelect,
            FormCheckbox,
        },
        props: {
            modalId: String,
            create: {
                type: Function,
                default: () => Promise.resolve(),
            },
            update: {
                type: Function,
                default: () => Promise.resolve(),
            },
            initialValues: {
                type: Object,
                default: () => ({}),
            },
        },
        computed: {
            ...mapGetters('header', ['userCurrencySymbol']),
            title() {
                return this.initialValues.id ? 'Edit Occupancy Tax' : 'Add Occupancy Tax';
            },
            amountState() {
                return !!this.values.amount;
            },
            taxableBasesState() {
                return this.values.taxable_bases.length > 0;
            },
            taxTypeOptions() {
                return [{ value: null, text: 'Choose one', disabled: true }].concat(
                    Object.entries(PARTNER_AIRBNB_TAX_TYPE_MAPPING).map(([taxType, taxTypeLabel]) => ({
                        value: taxType,
                        text: taxTypeLabel,
                    }))
                );
            },
            amountTypeOptions() {
                return Object.entries(PARTNER_AIRBNB_AMOUNT_TYPE_MAPPING).map(([amountType, amountTypeLabel]) => ({
                    value: amountType,
                    text: amountTypeLabel,
                }));
            },
            taxableBasesOptions() {
                return Object.entries(PARTNER_AIRBNB_TAXABLE_BASES_MAPPING).map(([taxableBase, taxableBaseLabel]) => ({
                    value: taxableBase,
                    text: taxableBaseLabel,
                }));
            },
        },
        mounted() {
            this.values = this.getInitialValues();
        },
        data() {
            return {
                loading: false,
                values: { attestation: false },
                PERCENT_PER_RESERVATION: PARTNER_AIRBNB_AMOUNT_TYPE_PERCENT_PER_RESERVATION,
                FLAT_PER_GUEST_PER_NIGHT: PARTNER_AIRBNB_AMOUNT_TYPE_FLAT_PER_GUEST_PER_NIGHT,
            };
        },
        methods: {
            hideModal() {
                this.$bvModal.hide(this.modalId);
            },
            getInitialValues() {
                return {
                    id: this.initialValues.id || null,
                    name: this.initialValues.name || '',
                    tax_type: this.initialValues.tax_type || null,
                    amount: this.initialValues.amount || null,
                    amount_type: this.initialValues.amount_type || this.PERCENT_PER_RESERVATION,
                    taxable_bases: this.initialValues.taxable_bases || [],
                    business_tax_id: this.initialValues.business_tax_id || '',
                    tot_registration_id: this.initialValues.tot_registration_id || '',
                    long_term_stay_exemption: this.initialValues.long_term_stay_exemption || null,
                    only_first_nights_exemption: this.initialValues.only_first_nights_exemption || null,
                    max_cap_per_person_per_night: this.initialValues.max_cap_per_person_per_night || null,
                    attestation: this.initialValues.attestation || false,
                };
            },
            async save() {
                this.loading = true;

                try {
                    const response = await (this.values.id ? this.update(this.values) : this.create(this.values));
                    this.$emit('success-save', response.data);
                    this.hideModal();
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);

                    if (generic) {
                        this.errorToast({ text: generic });
                    }
                    if (!isEmpty(fields)) {
                        this.$refs.form.setErrors(fields);
                    }
                } finally {
                    this.loading = false;
                }
            },
            setData() {
                this.$refs.form.setValues(this.values);
            },
        },
        watch: {
            initialValues() {
                this.values = this.getInitialValues();
            },
        },
    };
</script>

<style lang="scss" scoped>
    .subtitles {
        display: inline-block;
        font-size: 12px;
        line-height: 120%;
    }

    :deep(.form-checkbox-group) {
        color: #404040 !important;
        text-decoration: none !important;
        border: none;
        width: 100%;
        height: 100%;
        background-color: white;
        padding-bottom: 10px;
        border-radius: 5px;
    }
</style>
